import { client } from "./supabase";

const ONSIGN_PRECIO_POR_PANTALLA = 10.99;

const contarPantallasActivas = async (pais, fecha) => {
  const [edificiosRes, carteleriaRes] = await Promise.all([
    client
      .from("edificios")
      .select("pantallas, cowork, espera, totems, inicio_servicio")
      .eq("pais", pais)
      .eq("estado", "OPERATIVO")
      .lte("inicio_servicio", fecha),
    client
      .from("negocios_carteleria")
      .select("cantidad_pantallas, fecha_inicio, plazo_contrato_meses")
      .eq("pais", pais)
      .eq("eliminada", false)
      .or("estado.eq.Activo,estado.eq.activo,estado.eq.100%")
      .lte("fecha_inicio", fecha),
  ]);

  let total = 0;

  for (const e of edificiosRes.data || []) {
    total += (Number(e.pantallas) || 0) + (Number(e.cowork) || 0) + (Number(e.espera) || 0) + (Number(e.totems) || 0);
  }

  for (const n of carteleriaRes.data || []) {
    if (!n.cantidad_pantallas) continue;
    if (n.plazo_contrato_meses && n.fecha_inicio) {
      const fechaFin = new Date(n.fecha_inicio);
      fechaFin.setMonth(fechaFin.getMonth() + parseInt(n.plazo_contrato_meses, 10));
      const fechaFinStr = fechaFin.toISOString().split("T")[0];
      if (fechaFinStr < fecha) continue;
    }
    total += Number(n.cantidad_pantallas);
  }

  return total;
};

export const getProveedoresRecurrentesEntries = async (
  pais,
  monedaLocal = "CLP",
  facturasRecibidas = [],
  facturasExternas = [],
  // Regla 10.163 (solo Chile): movimientos_caja para conciliar los recurrentes por pago real.
  movimientosCaja = []
) => {
  const { data: proveedores, error } = await client
    .from("proveedores")
    .select("id, nombre, alias, monto_recurrente, moneda_recurrente, moneda_default, dia_pago, periodicidad")
    .eq("pais", pais)
    .eq("habilitado", true)
    .eq("es_recurrente", true);

  if (error) throw error;

  // Set de `${proveedor_id}-YYYY-MM` que ya tienen factura (SII o externa) en ese mes calendario.
  // Si existe, no generamos entry sintética para evitar doble conteo con la fila de "Por pagar".
  // Comparación SIEMPRE por proveedor_id numérico.
  const mesProveedorConFactura = new Set();
  for (const f of facturasRecibidas) {
    if (!f.proveedor_id || !f.fecha_emision) continue;
    mesProveedorConFactura.add(`${f.proveedor_id}-${f.fecha_emision.slice(0, 7)}`);
  }
  // Externas: preferir periodo_mes (mes facturado, semánticamente correcto) y caer a
  // fecha_emision cuando no está poblado. Esto cubre proveedores extranjeros (OnSign,
  // etc.) que no tienen DTE SII y por lo tanto nunca aparecen en facturasRecibidas.
  for (const f of facturasExternas) {
    if (!f.proveedor_id) continue;
    const mesRef = f.periodo_mes ? f.periodo_mes.slice(0, 7) : f.fecha_emision ? f.fecha_emision.slice(0, 7) : null;
    if (!mesRef) continue;
    mesProveedorConFactura.add(`${f.proveedor_id}-${mesRef}`);
  }

  // P6 (Fase 1) — El monto de la proyección de recurrentes GENÉRICOS (no OnSign) es el PROMEDIO de los
  // meses con factura dentro de la ventana de los 3 meses calendario más recientes CONTANDO el mes en
  // curso (ej: julio → {mayo, junio, julio}). Se suman facturas_sii_recibidas + facturas_externas del
  // proveedor en su moneda operativa (moneda_default); un mes con 2 folios suma ambos. El divisor es la
  // cantidad de meses PRESENTES (con al menos una factura), NO 3 fijo: así un proveedor mensual estable
  // proyecta su mensualidad aunque la factura del mes en curso todavía no esté cargada. Fallback a
  // monto_recurrente si no hay ninguna factura en la ventana. Comparación por proveedor_id numérico
  // (regla 10.7). NOTA: query DEDICADO — no se reutilizan los arrays facturasRecibidas/facturasExternas
  // del parámetro (llegan filtrados por estado: getFacturasRecibidas usa estado_not:"ignorada", sirven
  // para otros usos). facturas_sii_recibidas NO tiene columna moneda: sus montos están siempre en moneda
  // local, por eso solo se suman cuando moneda_default === monedaLocal.
  const provIds = (proveedores || []).map((p) => p.id);
  const monedaDefaultPorProv = new Map(
    (proveedores || []).map((p) => [p.id, p.moneda_default || p.moneda_recurrente || monedaLocal])
  );

  // Ventana: los 3 meses calendario más recientes contando el mes en curso (como strings YYYY-MM,
  // construidos desde getFullYear/getMonth para NO pasar por toISOString y evitar corrimientos de TZ).
  const hoyBase = new Date();
  const ventanaMeses = [];
  for (let i = 0; i < 3; i += 1) {
    const d = new Date(hoyBase.getFullYear(), hoyBase.getMonth() - i, 1);
    ventanaMeses.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  // Piso del query: primer día del mes (hoy − 3), un mes de margen sobre la ventana para no perder
  // externas cuyo periodo_mes cae en la ventana pero con fecha_emision algo anterior.
  const pisoD = new Date(hoyBase.getFullYear(), hoyBase.getMonth() - 3, 1);
  const desdeFecha = `${pisoD.getFullYear()}-${String(pisoD.getMonth() + 1).padStart(2, "0")}-01`;

  // key `${proveedor_id}-YYYY-MM` → suma de montos en moneda operativa. La existencia de la key marca
  // el mes como "presente" (tuvo al menos una factura), aunque la suma diera 0.
  const sumaProvMes = new Map();
  const acumular = (provId, mesRef, monto) => {
    if (!provId || !mesRef) return;
    const key = `${provId}-${mesRef}`;
    sumaProvMes.set(key, (sumaProvMes.get(key) || 0) + monto);
  };

  if (provIds.length > 0) {
    const [siiVentanaRes, extVentanaRes] = await Promise.all([
      client
        .from("facturas_sii_recibidas")
        .select("proveedor_id, monto_total, fecha_emision")
        .eq("pais", pais)
        .in("proveedor_id", provIds)
        .gte("fecha_emision", desdeFecha),
      client
        .from("facturas_externas")
        .select("proveedor_id, monto_total, moneda, fecha_emision, periodo_mes")
        .eq("pais", pais)
        .in("proveedor_id", provIds)
        .neq("estado", "anulada")
        .gte("fecha_emision", desdeFecha),
    ]);

    for (const f of siiVentanaRes.data || []) {
      // SII siempre en moneda local; solo aplica si el proveedor opera en esa moneda.
      if (monedaDefaultPorProv.get(f.proveedor_id) !== monedaLocal) continue;
      if (!f.fecha_emision) continue;
      acumular(f.proveedor_id, f.fecha_emision.slice(0, 7), Number(f.monto_total || 0));
    }
    for (const f of extVentanaRes.data || []) {
      if ((f.moneda || monedaLocal) !== monedaDefaultPorProv.get(f.proveedor_id)) continue;
      const mesRef = f.periodo_mes ? f.periodo_mes.slice(0, 7) : f.fecha_emision ? f.fecha_emision.slice(0, 7) : null;
      acumular(f.proveedor_id, mesRef, Number(f.monto_total || 0));
    }
  }

  // Promedio por proveedor sobre los meses PRESENTES de la ventana (divisor = cantidad de meses con
  // factura). Map proveedor_id → monto promedio (2 decimales). Sin meses presentes → no entra al map
  // y el consumidor cae a monto_recurrente.
  const montoPromedioPorProv = new Map();
  for (const prov of proveedores || []) {
    let suma = 0;
    let presentes = 0;
    for (const wm of ventanaMeses) {
      const key = `${prov.id}-${wm}`;
      if (sumaProvMes.has(key)) {
        suma += sumaProvMes.get(key);
        presentes += 1;
      }
    }
    if (presentes > 0) montoPromedioPorProv.set(prov.id, Math.round((suma / presentes) * 100) / 100);
  }

  const hoy = new Date().toISOString().split("T")[0];
  const hoyDate = new Date();
  // Generar entries hasta el 31/dic del año siguiente para alimentar la vista completa
  // (sin filtro temporal) del panel de Movimientos Futuros.
  const finAnio = new Date(hoyDate.getFullYear() + 1, 11, 31);
  const entries = [];

  // OnSign se detecta por ID, no por nombre (regla 10.7). IDs reales en DB: Chile 18, Perú 56.
  const ONSIGN_ID = pais === "Perú" ? 56 : 18;
  const onsignProv = (proveedores || []).find((p) => p.id === ONSIGN_ID);

  // Regla 10.163 (solo Chile): el rango arranca en 2026-01 para poder mostrar recurrentes cerrados
  // impagos (rojo). Perú mantiene el arranque en el mes actual (comportamiento previo).
  const esChile = pais === "Chile";
  const meses = [];
  const cursor = esChile ? new Date(2026, 0, 1) : new Date(hoyDate.getFullYear(), hoyDate.getMonth(), 1);
  while (cursor <= finAnio) {
    meses.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const pantallasXMes = {};
  if (onsignProv) {
    for (const mes of meses) {
      const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];
      // eslint-disable-next-line no-await-in-loop
      pantallasXMes[fechaStr] = await contarPantallasActivas(pais, fechaStr);
    }
  }

  // Conciliación de pago (regla 10.163, solo Chile): mapa proveedor_id → Set de meses "YYYY-MM"
  // saldados por un pago_proveedor en movimientos_caja (match por entidad_id, regla 8), con
  // tolerancia de straddle (el pago de un mes puede caer en los primeros 15 días del mes siguiente).
  // Claiming greedy: cada pago concilia UN solo mes (evita que un pago tape dos meses).
  const mesesKeys = meses.map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  const straddleHasta = (mesKey) => {
    const [y, mm] = mesKey.split("-").map(Number);
    const ny = mm === 12 ? y + 1 : y;
    const nm = mm === 12 ? 1 : mm + 1;
    return `${ny}-${String(nm).padStart(2, "0")}-15`;
  };
  const mesesPagadosPorProv = new Map();
  if (esChile) {
    const pagosPorProv = new Map();
    for (const m of movimientosCaja || []) {
      if (m.entidad_tipo !== "proveedor" || m.categoria_pago !== "pago_proveedor" || !m.entidad_id || !m.fecha) {
        continue;
      }
      const arr = pagosPorProv.get(m.entidad_id) || [];
      arr.push(m.fecha.slice(0, 10));
      pagosPorProv.set(m.entidad_id, arr);
    }
    for (const [provId, fechas] of pagosPorProv) {
      const ordenadas = [...fechas].sort();
      const claimed = new Set();
      const pagadas = new Set();
      for (const mesKey of mesesKeys) {
        const lo = `${mesKey}-01`;
        const hi = straddleHasta(mesKey);
        for (let i = 0; i < ordenadas.length; i += 1) {
          if (claimed.has(i)) continue;
          if (ordenadas[i] >= lo && ordenadas[i] <= hi) {
            claimed.add(i);
            pagadas.add(mesKey);
            break;
          }
        }
      }
      mesesPagadosPorProv.set(provId, pagadas);
    }
  }

  // Primer mes de actividad por proveedor (factura o pago más antiguo). No se proyecta el recurrente
  // antes de ese mes → evita falsos vencidos de proveedores de alta reciente (ej. Clay, onboarded en
  // abril, no debe mostrar rojo en ene/feb/mar). Solo Chile.
  const primerMesPorProv = new Map();
  if (esChile) {
    const registrar = (provId, mesKey) => {
      const actual = primerMesPorProv.get(provId);
      if (!actual || mesKey < actual) primerMesPorProv.set(provId, mesKey);
    };
    // Facturas: mesProveedorConFactura tiene keys `${provId}-YYYY-MM`.
    for (const key of mesProveedorConFactura) {
      const mesKey = key.slice(-7);
      const provId = Number(key.slice(0, key.length - 8));
      if (provId) registrar(provId, mesKey);
    }
    // Pagos.
    for (const m of movimientosCaja || []) {
      if (m.entidad_tipo === "proveedor" && m.categoria_pago === "pago_proveedor" && m.entidad_id && m.fecha) {
        registrar(m.entidad_id, m.fecha.slice(0, 7));
      }
    }
  }

  for (const prov of proveedores || []) {
    const esOnSign = prov.id === ONSIGN_ID;
    const esMensual = prov.periodicidad === "mensual";

    for (const mes of meses) {
      const diaPago = prov.dia_pago || 30;
      const fechaPagoDate = new Date(mes.getFullYear(), mes.getMonth(), diaPago);

      if (fechaPagoDate.getMonth() !== mes.getMonth()) {
        fechaPagoDate.setDate(0);
      }

      const fechaPago = fechaPagoDate.toISOString().split("T")[0];
      const mesKey = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, "0")}`;

      // Corte 2026-01: no proyectar antes (saldado por saldo inicial).
      if (mesKey < "2026-01") continue;

      // Anti alta-reciente (solo Chile): no proyectar antes del primer mes con actividad real del
      // proveedor (factura o pago) → evita falsos vencidos de proveedores nuevos.
      if (esChile && esMensual) {
        const primerMes = primerMesPorProv.get(prov.id);
        if (primerMes && mesKey < primerMes) continue;
      }

      // Dedup: si ya hay factura (SII/externa) del proveedor en ese mes, la fila la muestra "Por pagar".
      if (mesProveedorConFactura.has(`${prov.id}-${mesKey}`)) continue;

      if (esChile && esMensual) {
        // Regla 10.163: el recurrente sale de la proyección SOLO si está saldado (pago_proveedor
        // conciliado al mes). No saldado → se genera; si la fecha ya pasó, el render lo pinta rojo
        // (vencido impago). NO se descarta por fecha.
        if (mesesPagadosPorProv.get(prov.id)?.has(mesKey)) continue;
      } else if (fechaPago <= hoy) {
        // Perú y no-mensuales: comportamiento previo (solo futuro, sin marcar vencidos).
        continue;
      }

      let monto;
      let monedaEntry;

      if (esOnSign) {
        const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];
        const pantallas = pantallasXMes[ultimoDia] || 0;
        monto = Math.round(pantallas * ONSIGN_PRECIO_POR_PANTALLA * 100) / 100;
        monedaEntry = prov.moneda_default || prov.moneda_recurrente || monedaLocal;
      } else {
        // P6 (Fase 1) — prioridad del monto: (1) promedio de la ventana si hubo facturas → (2)
        // monto_recurrente como fallback → (3) skip si ambos 0/null (no proyecta basura). La moneda ya
        // NO se deriva de una factura: es la moneda operativa del proveedor (moneda_default).
        const promedio = montoPromedioPorProv.get(prov.id);
        monto = promedio != null ? promedio : Number(prov.monto_recurrente || 0);
        monedaEntry = prov.moneda_default || prov.moneda_recurrente || monedaLocal;
      }

      if (monto <= 0) continue;

      const ultimoDiaKey = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];

      entries.push({
        id: `recurrente-${prov.id}-${fechaPago}`,
        fecha_display: fechaPago,
        importeEnMoneda: -monto,
        moneda_recurrente: monedaEntry,
        razon_social_receptor: esOnSign
          ? `${prov.alias || prov.nombre} (${pantallasXMes[ultimoDiaKey] || 0} licencias)`
          : prov.alias || prov.nombre,
        tipo: "proveedor",
        es_recurrente: true,
      });
    }
  }

  return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
};
